import {
  isSameDay,
  parseLocalDate,
  startOfDay,
  toISODate,
} from "./calendarDates";

/**
 * @typedef {import('./calendarFallback2026').CalendarTerm} CalendarTerm
 * @typedef {import('./calendarFallback2026').CalendarClosure} CalendarClosure
 * @typedef {{
 *   year: number | null,
 *   title: string,
 *   terms: CalendarTerm[],
 *   closures: CalendarClosure[],
 * }} CalendarConfig
 */

/**
 * @param {CalendarConfig} config
 * @returns {{ dates: Set<string>, labels: Map<string, string> }}
 */
export function buildClosureLookup(config) {
  const dates = new Set();
  const labels = new Map();

  (config.closures || []).forEach(({ date, label }) => {
    if (!date) return;
    dates.add(date);
    if (label) {
      labels.set(date, label);
    }
  });

  return { dates, labels };
}

/**
 * @param {string} iso
 * @param {Map<string, string>} labelMap
 * @returns {string}
 */
export function getClosedLabel(iso, labelMap) {
  return labelMap.get(iso) || "Closed";
}

/**
 * @param {CalendarConfig} config
 * @returns {Set<string>}
 */
export function buildClosedDateSet(config) {
  return buildClosureLookup(config).dates;
}

/**
 * @param {Date} date
 * @param {CalendarTerm} term
 * @returns {boolean}
 */
export function isDateInTerm(date, term) {
  const day = startOfDay(date);
  const start = startOfDay(parseLocalDate(term.startDate));
  const end = startOfDay(parseLocalDate(term.endDate));
  return day >= start && day <= end;
}

/**
 * @param {Date} date
 * @param {CalendarTerm[]} terms
 * @returns {CalendarTerm | undefined}
 */
export function findTermForDate(date, terms) {
  return terms.find((term) => isDateInTerm(date, term));
}

/**
 * @param {CalendarTerm[]} terms
 * @param {Date} [today=new Date()]
 * @returns {CalendarTerm | undefined}
 */
export function findCurrentTerm(terms, today = new Date()) {
  return findTermForDate(today, terms);
}

/**
 * Last Friday on or before the term end date.
 * @param {CalendarTerm} term
 * @returns {Date}
 */
export function getAutoDraftDate(term) {
  const end = startOfDay(parseLocalDate(term.endDate));
  const cursor = new Date(end);

  while (cursor.getDay() !== 5) {
    cursor.setDate(cursor.getDate() - 1);
  }

  return cursor;
}

/**
 * @param {CalendarConfig} config
 * @returns {Set<string>}
 */
export function buildAutoDraftDateSet(config) {
  const dates = config.terms.map((term) => toISODate(getAutoDraftDate(term)));
  return new Set(dates);
}

/**
 * @param {Date} date
 * @param {CalendarConfig} config
 * @param {Set<string>} closedDates
 * @returns {boolean}
 */
export function isClosedDay(date, config, closedDates) {
  const iso = toISODate(date);
  const term = findTermForDate(date, config.terms);

  if (!term) return false;
  return closedDates.has(iso);
}

/**
 * @param {Date} date
 * @param {CalendarConfig} config
 * @param {Set<string>} autoDraftDates
 * @param {Set<string>} closedDates
 * @param {Map<string, string>} closureLabels
 * @returns {{
 *   isClosed: boolean,
 *   isAutoDraft: boolean,
 *   closureLabel?: string,
 *   term?: CalendarTerm,
 *   isToday: boolean,
 * }}
 */
export function getDayInfo(
  date,
  config,
  autoDraftDates,
  closedDates,
  closureLabels,
  today = new Date()
) {
  const term = findTermForDate(date, config.terms);
  const iso = toISODate(date);
  const isAutoDraft = autoDraftDates.has(iso);
  const isClosed = isClosedDay(date, config, closedDates);

  return {
    isClosed,
    isAutoDraft,
    closureLabel: isClosed ? getClosedLabel(iso, closureLabels) : undefined,
    term,
    isToday: isSameDay(date, today),
  };
}

/** @typedef {{ date: Date, type: 'closed' | 'autoDraft', term?: CalendarTerm, label: string }} CalendarListItem */

/**
 * @param {CalendarConfig} config
 * @param {Date} [fromDate=new Date()]
 * @returns {CalendarListItem[]}
 */
export function buildUpcomingList(config, fromDate = new Date()) {
  const today = startOfDay(fromDate);
  const autoDraftSet = buildAutoDraftDateSet(config);
  const { dates: closedSet, labels } = buildClosureLookup(config);
  /** @type {CalendarListItem[]} */
  const items = [];

  config.terms.forEach((term) => {
    const autoDraftDate = getAutoDraftDate(term);
    if (autoDraftDate >= today) {
      const iso = toISODate(autoDraftDate);
      const closureNote = labels.get(iso);
      items.push({
        date: autoDraftDate,
        type: "autoDraft",
        term,
        label: closureNote
          ? `${closureNote} · Fee auto-draft`
          : "Fee auto-draft",
      });
    }
  });

  closedSet.forEach((iso) => {
    if (autoDraftSet.has(iso)) return;

    const date = parseLocalDate(iso);
    if (date < today) return;

    const term = findTermForDate(date, config.terms);
    const label = getClosedLabel(iso, labels);

    items.push({
      date,
      type: "closed",
      term,
      label,
    });
  });

  return items.sort((a, b) => a.date - b.date);
}

/**
 * @param {CalendarConfig} config
 * @param {Set<string>} closedDates
 * @param {Date} [today]
 * @returns {boolean}
 */
export function isGymOpenToday(config, closedDates, today = new Date()) {
  const term = findTermForDate(today, config.terms);
  if (!term) return false;
  return !isClosedDay(today, config, closedDates);
}

/**
 * @param {CalendarConfig} config
 * @param {Date} [fromDate]
 * @returns {CalendarListItem | undefined}
 */
export function findNextAutoDraft(config, fromDate = new Date()) {
  const today = startOfDay(fromDate);
  return buildUpcomingList(config, today).find((item) => item.type === "autoDraft");
}

/**
 * @param {Date} date
 * @returns {string}
 */
export function formatICSDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

/**
 * @param {{ title: string, date: Date, description?: string }} event
 */
export function downloadICS(event) {
  const dt = formatICSDate(event.date);
  const description = (event.description || "").replace(/\n/g, "\\n");
  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Ace Gymnastics//Calendar//EN",
    "BEGIN:VEVENT",
    `DTSTART;VALUE=DATE:${dt}`,
    `DTEND;VALUE=DATE:${dt}`,
    `SUMMARY:${event.title}`,
    description ? `DESCRIPTION:${description}` : null,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");

  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${event.title.replace(/\s+/g, "-").toLowerCase()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
